import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, AlertTriangle, Flag, Lock, Users, Scale } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Seo } from '@/components/Seo';
import { AutoTranslate } from '@/components/AutoTranslate';
import { MiniLanguageToggle } from '@/components/MiniLanguageToggle';

const SafetyStandardsPage = () => {
  const lastUpdated = "April 14, 2026";

  return (
    <div className="min-h-screen bg-background">
      <Seo
        path="/safety"
        title="Safety Standards — ClearCommunicationApp"
        description="Our safety standards: how we protect users, handle reports, and maintain a respectful, trauma-informed community."
      />
      <div className="container max-w-3xl py-8 px-4">
        <div className="flex items-center justify-between mb-6">
          <Link to="/faqs">
            <Button variant="ghost" className="-ml-2">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to FAQs
            </Button>
          </Link>
          <MiniLanguageToggle />
        </div>
        <AutoTranslate>

        <div className="flex items-center gap-3 mb-2">
          <Shield className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">Safety Standards</h1>
        </div>
        <p className="text-muted-foreground mb-8">
          ClearCommunicationApp's standards against Child Sexual Abuse and Exploitation (CSAE)
        </p>
        <p className="text-xs text-muted-foreground mb-8">Last updated: {lastUpdated}</p>

        {/* Introduction */}
        <section className="bg-card rounded-xl p-6 border border-border mb-6">
          <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Our Commitment
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            ClearCommunicationApp is committed to maintaining a safe environment for all users. We have zero tolerance for child sexual abuse and exploitation (CSAE) content or behavior of any kind on our platform. This document outlines our policies, procedures, and measures to prevent, detect, and respond to CSAE.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            Our app is designed to help neurodivergent individuals decode everyday communication. We take our responsibility to protect minors and vulnerable users extremely seriously.
          </p>
        </section>

        {/* Prohibited Content & Conduct */}
        <section className="bg-card rounded-xl p-6 border border-border mb-6">
          <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Prohibited Content and Conduct
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-3">
            The following are strictly prohibited on ClearCommunicationApp:
          </p>
          <ul className="space-y-2 text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-red-500 mt-1 font-bold">•</span>
              <span>Any content that sexually exploits or endangers children, including but not limited to child sexual abuse material (CSAM)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-500 mt-1 font-bold">•</span>
              <span>Grooming behavior — any attempt to build a relationship with a minor for the purpose of sexual abuse or exploitation</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-500 mt-1 font-bold">•</span>
              <span>Sextortion or any form of coercion targeting minors</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-500 mt-1 font-bold">•</span>
              <span>Sharing, distributing, or soliciting exploitative material involving minors</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-500 mt-1 font-bold">•</span>
              <span>Any communication that sexualizes minors in any way</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-500 mt-1 font-bold">•</span>
              <span>AI-generated CSAM or deepfake content involving minors</span>
            </li>
          </ul>
        </section>

        {/* Detection & Prevention */}
        <section className="bg-card rounded-xl p-6 border border-border mb-6">
          <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
            <Lock className="h-5 w-5 text-primary" />
            Detection and Prevention Measures
          </h2>
          <ul className="space-y-3 text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 font-bold">•</span>
              <span><strong className="text-foreground">Content moderation:</strong> All user-generated content, including feedback submissions and custom data, is subject to moderation and review.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 font-bold">•</span>
              <span><strong className="text-foreground">No direct messaging:</strong> Our platform does not include direct messaging, chat rooms, or any peer-to-peer communication features that could be exploited for grooming or abuse.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 font-bold">•</span>
              <span><strong className="text-foreground">No user-uploaded media:</strong> Users cannot upload images, videos, or other media files. Profile avatars are generated from pre-set options only.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 font-bold">•</span>
              <span><strong className="text-foreground">Account monitoring:</strong> We monitor for suspicious account creation patterns and behavior anomalies.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 font-bold">•</span>
              <span><strong className="text-foreground">Regular audits:</strong> We conduct periodic reviews of our platform to identify and close any potential vectors for abuse.</span>
            </li>
          </ul>
        </section>

        {/* Reporting */}
        <section className="bg-card rounded-xl p-6 border border-border mb-6">
          <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
            <Flag className="h-5 w-5 text-amber-500" />
            Reporting Mechanisms
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-3">
            If you encounter any content or behavior that you believe may involve the exploitation of children, please report it immediately through any of the following channels:
          </p>
          <ul className="space-y-2 text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-amber-500 mt-1 font-bold">•</span>
              <span><strong className="text-foreground">Email:</strong> <a href="mailto:Safety@ClearCommunicationApp.com" className="text-primary hover:underline">Safety@ClearCommunicationApp.com</a></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-500 mt-1 font-bold">•</span>
              <span><strong className="text-foreground">In-app:</strong> Use the <Link to="/feedback" className="text-primary hover:underline">Feedback / Report</Link> page to flag concerns</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-500 mt-1 font-bold">•</span>
              <span><strong className="text-foreground">NCMEC CyberTipline:</strong> <a href="https://www.missingkids.org/gethelpnow/cybertipline" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">www.missingkids.org/gethelpnow/cybertipline</a></span>
            </li>
          </ul>
          <p className="text-muted-foreground leading-relaxed mt-4">
            All reports are treated with the utmost urgency and confidentiality. We aim to respond to safety reports within 24 hours.
          </p>
        </section>

        {/* Enforcement */}
        <section className="bg-card rounded-xl p-6 border border-border mb-6">
          <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
            <Scale className="h-5 w-5 text-primary" />
            Enforcement Actions
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-3">
            When violations are identified, we take swift and decisive action:
          </p>
          <ul className="space-y-2 text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 font-bold">•</span>
              <span><strong className="text-foreground">Immediate account termination</strong> for any user found to be involved in CSAE-related activity</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 font-bold">•</span>
              <span><strong className="text-foreground">Content removal</strong> — any violating content is removed immediately upon discovery</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 font-bold">•</span>
              <span><strong className="text-foreground">Law enforcement referral</strong> — all confirmed or suspected CSAE incidents are reported to the National Center for Missing & Exploited Children (NCMEC) and relevant law enforcement authorities</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 font-bold">•</span>
              <span><strong className="text-foreground">Evidence preservation</strong> — relevant data is preserved in accordance with legal requirements to support law enforcement investigations</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 font-bold">•</span>
              <span><strong className="text-foreground">Permanent ban</strong> — terminated accounts are permanently barred from creating new accounts</span>
            </li>
          </ul>
        </section>

        {/* Team & Training */}
        <section className="bg-card rounded-xl p-6 border border-border mb-6">
          <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Team Accountability
          </h2>
          <ul className="space-y-2 text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 font-bold">•</span>
              <span>All team members involved in content moderation receive training on identifying and handling CSAE-related content</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 font-bold">•</span>
              <span>Our safety policies are reviewed and updated at least annually, or more frequently as needed</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 font-bold">•</span>
              <span>We maintain compliance with applicable laws including the EARN IT Act, PROTECT Act, and COPPA</span>
            </li>
          </ul>
        </section>

        {/* Contact */}
        <section className="bg-card rounded-xl p-6 border border-border mb-6">
          <h2 className="text-xl font-semibold mb-3">Contact</h2>
          <p className="text-muted-foreground leading-relaxed">
            For questions about our safety standards, please contact us at{' '}
            <a href="mailto:Safety@ClearCommunicationApp.com" className="text-primary hover:underline">Safety@ClearCommunicationApp.com</a>.
          </p>
        </section>

        {/* Footer Links */}
        <div className="flex justify-center gap-4 text-xs text-muted-foreground mt-8 mb-4">
          <Link to="/privacy" className="hover:text-primary transition-colors">Privacy Policy</Link>
          <span>|</span>
          <Link to="/terms" className="hover:text-primary transition-colors">Terms of Service</Link>
          <span>|</span>
          <Link to="/faqs" className="hover:text-primary transition-colors">FAQs</Link>
        </div>
        </AutoTranslate>
      </div>
    </div>
  );
};

export default SafetyStandardsPage;
